import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  ApiProductionLine,
  IApiProductionLine,
} from 'src/app/models_new/classes/api-models/ApiProduction_line';
import { ProdlineApiService } from 'src/app/services/api/prodline-api.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { NotificationService } from 'src/app/services/notification.service';
import omit from 'lodash-es/omit';
import { pagesPATH } from 'src/app/models_new/config/pages';
import { Location } from '@angular/common';
import { StateService } from 'src/app/auth/state.service';

@Component({
  selector: 'app-production-line',
  templateUrl: './production-line.component.html',
  styleUrls: ['./production-line.component.scss'],
})
export class ProductionLineComponent implements OnInit {
  productionLine$: Observable<ApiProductionLine>;
  cardMode$: Observable<'create' | 'view' | 'edit'>;
  pageTitle$: Observable<string>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private prodlineApi: ProdlineApiService,
    private errorHandler: ErrorHandlerService,
    private notifier: NotificationService,
    private location: Location,
    private stateService: StateService
  ) {}

  ngOnInit(): void {
    this.loadContent();
  }

  private loadContent(): void {
    this.productionLine$ = combineLatest([
      this.route.params,
      this.stateService.getCustomerOrSalesOrganization().pipe(),
    ]).pipe(
      switchMap(([params, activeOrganization]) => {
        if (params.id === 'new') {
          return of(
            new ApiProductionLine({
              id: undefined,
              name: '',
              data: {
                cpm: 0,
                hop: 0,
                mop: 0,
                per: 'day',
              },
              owner_id: undefined,
              product_ids: [],
              organization_id: activeOrganization.id,
              updated_at: undefined,
              production_line_palletizing_projects: undefined,
            })
          );
        } else {
          return this.prodlineApi
            .fetchProductionLine(params.id)
            .pipe(map((p: IApiProductionLine) => new ApiProductionLine(p)));
        }
      })
    );

    this.cardMode$ = combineLatest([
      this.route.queryParamMap,
      this.route.params,
    ]).pipe(
      map(([queryParamMap, params]) => {
        if (params.id === 'new') {
          return 'create';
        }
        if (queryParamMap.has('edit')) {
          return 'edit';
        }
        return 'view';
      })
    );

    this.pageTitle$ = this.cardMode$.pipe(
      switchMap((mode) => {
        if (mode === 'create') {
          return of('Create production line');
        }

        // For all other purposes we use the name of the production
        return this.productionLine$.pipe(map((pl) => pl.name));
      })
    );
  }

  onEdit() {
    // Just update the query params
    this.router.navigate([], {
      relativeTo: this.route,
      replaceUrl: true,
      queryParams: { edit: true },
      queryParamsHandling: 'merge',
    });
  }

  switchToView() {
    // Remove 'edit' from query params
    this.router.navigate([], {
      relativeTo: this.route,
      replaceUrl: true,
      queryParams: omit(this.route.snapshot.queryParams, ['edit']),
    });
  }

  navigateBack() {
    this.location.back();
  }

  onSave(productionLine: ApiProductionLine): void {
    let operation;
    if (productionLine.id) {
      operation = this.prodlineApi.updateProductionLine(
        productionLine.id,
        productionLine.name,
        productionLine.data
      );
    } else {
      operation = this.prodlineApi.createProductionLine(
        productionLine.name,
        productionLine.data,
        productionLine.organization_id
      );
    }
    operation.subscribe({
      next: (response) => {
        if (response && response.id) {
          this.loadContent();
          this.notifier.showSuccess(
            `Production line ${productionLine.name} added to the project!`
          );
          this.router.navigate([pagesPATH.PRODUCTION_LINES]);
        }
        // Remove 'edit' from query params
        this.switchToView();
      },
      error: (error) => {
        this.errorHandler.handleError(error);
        this.notifier.showError('Failed to save production line changes');
      },
    });
  }
}
